import type {
  AnnexureDefinitionPayload,
  AnnexureFlatQuestionDef,
  AnnexurePeriodicSectionDef,
  AnnexureQuestionnaireQuestionDef,
  AnnexureQuestionnaireSectionDef,
  AnnexureQuestionnaireStructure,
} from '../types';
import { decodeHtmlEntitiesDeep } from './htmlEntities';
import { annexureCodeUsesDesignerUi, ob2AnnexureIdForApiCode } from '../pages/vms/designerAnnexureFormMap';
import {
  A10_SECTIONS,
  A6_SECTIONS,
  A7_SECTIONS,
  A8_SECTIONS,
  A12_SECTIONS,
  ONGOING_VENDOR_REVIEW_ANNEXURE_FORMS,
  type OngoingVendorReviewAnnexureFormDefinition,
  type OngoingVendorReviewAnnexureSection,
} from '../pages/vms/ongoingVendorReviewAnnexureForms';
export const ANNEXURE_10_PREVIEW_INTRO =
  'Does any defect or disruption in service or failure in performance of the service provider could materially impair or impact -';
import {
  applyCode4VendorFillSections,
  getOb2AnnexureFormDefinition,
  getOb2AnnexureIdForApiCode,
  ob2AnnexureDefinitionForAdminView,
  ob2AnnexureDefinitionForVendorFill,
  type Ob2AnnexureFormDefinition,
  type Ob2AnnexureQuestion,
  type Ob2AnnexureQuestionnaireQuestion,
  type Ob2AnnexureQuestionnaireSection,
  type Ob2AnnexureQuestionnaireSubsection,
} from '../pages/vms/onboarding2AnnexureFormConfig';

/** System annexures shown on Annexure Management (numeric + 15A/15B). */
export const SYSTEM_ANNEXURE_MANAGEMENT_CODES = new Set([
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  '10',
  '11',
  '12',
  '13',
  '14',
  '15A',
  '15B',
]);

export function isSystemAnnexureManagementCode(apiCode: string): boolean {
  return SYSTEM_ANNEXURE_MANAGEMENT_CODES.has(String(apiCode ?? '').trim());
}

/** Structure tab: weighting footer is hidden in preview for Code 4-style UI only. */
export function structureEditorHidesWeightingScoreField(
  q: AnnexureQuestionnaireStructure | null | undefined,
  apiCode?: string
): boolean {
  const code = String(apiCode ?? '').trim();
  if (code === '4') return true;
  return q?.questionnaireUi === 'onboarding2Code4';
}

/** Decode legacy HTML entities and align editor metadata with preview (Code 4). */
export function normalizeQuestionnaireForEditor(
  apiCode: string,
  q: AnnexureQuestionnaireStructure
): AnnexureQuestionnaireStructure {
  const code = String(apiCode ?? '').trim();
  let next = decodeQuestionnaireStructureStrings(q);

  const isCode4 = code === '4' || next.questionnaireUi === 'onboarding2Code4';

  if (isCode4) {
    next = {
      ...next,
      questionnaireUi: 'onboarding2Code4',
      variant: next.variant ?? 'questionnaire-sections',
    };
    if (next.sections?.length) {
      next = {
        ...next,
        sections: next.sections.map(section => {
          const { weightingScoreDisplay: _omit, ...rest } = section;
          return rest;
        }),
      };
    }
  }

  if (next.periodicSections?.length) {
    next = {
      ...next,
      periodicSections: next.periodicSections.map(section => ({
        ...section,
        questions: section.questions.map(q => {
          const label =
            String(q.label ?? '').trim() || String(q.text ?? '').trim();
          return { ...q, label };
        }),
      })),
    };
  }

  return next;
}

/** Map editor fields to backend questionnaire schema before save (all annexure codes). */
export function normalizeQuestionnaireForSave(
  apiCode: string,
  q: AnnexureQuestionnaireStructure
): AnnexureQuestionnaireStructure {
  const code = String(apiCode ?? '').trim();
  let next: AnnexureQuestionnaireStructure = { ...q };

  if (next.periodicSections?.length) {
    next = {
      ...next,
      periodicSections: next.periodicSections.map(section => ({
        ...section,
        questions: (section.questions ?? []).map(question => {
          const label = String(question.label ?? question.text ?? '').trim();
          const id = String(question.id ?? '').trim() || `q_${Date.now()}`;
          return {
            ...question,
            id,
            label,
            text: label,
            storageRowId: question.storageRowId ?? id,
          };
        }),
      })),
    };
  }

  // Intro banner removed from Annexure 10 fill/preview; do not persist stale intro copy.
  if (code === '10') {
    const { introText: _omitIntro, ...rest } = next;
    next = rest;
  }

  return next;
}

/** Parse API/DB definition JSON and fix legacy sanitizeBody HTML entity encoding (all annexure codes). */
export function normalizeAnnexureDefinitionPayload(raw: unknown): AnnexureDefinitionPayload | null {
  if (raw == null) return null;
  let payload: AnnexureDefinitionPayload | null = null;
  if (typeof raw === 'string') {
    try {
      payload = JSON.parse(raw) as AnnexureDefinitionPayload;
    } catch {
      return null;
    }
  } else if (typeof raw === 'object' && !Array.isArray(raw)) {
    payload = raw as AnnexureDefinitionPayload;
  }
  if (!payload) return null;
  return decodeHtmlEntitiesDeep(payload);
}

export function hasQuestionnaireStructure(
  def: AnnexureDefinitionPayload | null | undefined
): boolean {
  const q = def?.questionnaire;
  if (!q) return false;
  return (
    (q.sections?.length ?? 0) > 0 ||
    (q.questions?.length ?? 0) > 0 ||
    (q.periodicSections?.length ?? 0) > 0
  );
}

function cloneQuestion(q: Ob2AnnexureQuestionnaireQuestion): AnnexureQuestionnaireQuestionDef {
  return {
    storageRowId: q.storageRowId,
    num: q.num,
    badgeNum: q.badgeNum,
    text: q.text,
    required: q.required,
    inlineUpload: q.inlineUpload,
    answerKind: q.answerKind,
    questionCode: q.questionCode,
    inlineComment: q.inlineComment,
    obligatory: q.obligatory,
    questionFullWidth: q.questionFullWidth,
    subsectionTitleBefore: q.subsectionTitleBefore,
    showRowUpload: q.showRowUpload,
  };
}

function cloneSubsection(s: Ob2AnnexureQuestionnaireSubsection) {
  return {
    id: s.id,
    title: s.title,
    questions: (s.questions ?? []).map(cloneQuestion),
  };
}

function cloneSection(s: Ob2AnnexureQuestionnaireSection): AnnexureQuestionnaireSectionDef {
  return {
    id: s.id,
    title: s.title,
    storageRowIds: s.storageRowIds,
    questions: (s.questions ?? []).map(cloneQuestion),
    subsections: s.subsections?.map(cloneSubsection),
    completionLabel: s.completionLabel,
    weightingScoreDisplay: s.weightingScoreDisplay,
    showSectionalComments: s.showSectionalComments,
    inlineSupportingDocument: s.inlineSupportingDocument,
    inlineSupportingDocumentVariant: s.inlineSupportingDocumentVariant,
    supportingDocumentUploadTone: s.supportingDocumentUploadTone,
    sectionNumberBadge: s.sectionNumberBadge,
    sectionTitleAllCaps: s.sectionTitleAllCaps,
    supportingDocumentTitleMuted: s.supportingDocumentTitleMuted,
    supportingDocumentSizeHint: s.supportingDocumentSizeHint,
    supportingDocumentUploadLinkText: s.supportingDocumentUploadLinkText,
    supportingDocumentStripHeading: s.supportingDocumentStripHeading,
    fullWidthSingleQuestionRow: s.fullWidthSingleQuestionRow,
    sectionFieldLabel: s.sectionFieldLabel,
    sectionUploadSlotId: s.sectionUploadSlotId,
    supportingDocumentBeforeQuestions: s.supportingDocumentBeforeQuestions,
    sectionUploadSlotIdEnd: s.sectionUploadSlotIdEnd,
  };
}

function cloneFlatQuestion(q: Ob2AnnexureQuestion): AnnexureFlatQuestionDef {
  return {
    id: q.id,
    text: q.text,
    storageRowId: q.storageRowId,
    fullWidth: q.fullWidth,
  };
}

function periodicSectionsToDef(sections: OngoingVendorReviewAnnexureSection[]): AnnexurePeriodicSectionDef[] {
  return sections.map(s => ({
    id: s.id,
    title: s.title,
    fieldMode: s.fieldMode,
    questions: s.questions.map(q => ({
      id: q.id,
      label: q.label,
      storageRowId: q.id,
      hasUpload: q.hasUpload,
      fullWidth: q.fullWidth,
    })),
  }));
}

function periodicDefToOngoing(sections: AnnexurePeriodicSectionDef[]): OngoingVendorReviewAnnexureSection[] {
  return sections.map(s => ({
    id: s.id,
    title: s.title,
    fieldMode: s.fieldMode,
    questions: s.questions.map(q => ({
      id: q.id,
      label: String(q.label ?? '').trim() || String(q.text ?? '').trim(),
      hasUpload: q.hasUpload,
      fullWidth: q.fullWidth,
    })),
  }));
}

/** Serialize designer preview config into DB-storable structure. */
export function ob2FormDefinitionToQuestionnaireStructure(
  ob2: Ob2AnnexureFormDefinition
): AnnexureQuestionnaireStructure {
  return {
    variant: ob2.variant,
    questionnaireUi: ob2.questionnaireUi,
    subtitle: ob2.subtitle,
    fieldLabel: ob2.fieldLabel,
    numberBadge: ob2.numberBadge,
    questionnaireLeadHeading: ob2.questionnaireLeadHeading,
    questionGridHeading: ob2.questionGridHeading,
    showRatingSummaryTable: ob2.showRatingSummaryTable,
    showSupportingUpload: ob2.showSupportingUpload,
    showApproversStakeholders: ob2.showApproversStakeholders,
    sections: ob2.sections?.map(cloneSection),
    questions: ob2.questions?.map(cloneFlatQuestion),
  };
}

function periodicStructureFromSections(
  variant: AnnexureQuestionnaireStructure['variant'],
  sections: OngoingVendorReviewAnnexureSection[],
  extra: Partial<AnnexureQuestionnaireStructure> = {}
): AnnexureQuestionnaireStructure {
  return {
    variant,
    fieldLabel: extra.fieldLabel ?? 'Response',
    numberBadge: 'square',
    periodicSections: periodicSectionsToDef(sections),
    ...extra,
  };
}

/** Finalized admin preview template for any system annexure on Annexure Management. */
export function questionnaireStructureFromPreview(apiCode: string): AnnexureQuestionnaireStructure | null {
  const code = String(apiCode ?? '').trim();
  if (code === '6') {
    return periodicStructureFromSections('kyc-periodic', A6_SECTIONS, {
      subtitle: 'KYC Due Diligence',
    });
  }
  const ob2Id = getOb2AnnexureIdForApiCode(code);
  const ob2 =
    ob2AnnexureDefinitionForAdminView(code) ??
    (ob2Id ? getOb2AnnexureFormDefinition(ob2Id) : undefined);
  if (ob2 && ((ob2.sections?.length ?? 0) > 0 || (ob2.questions?.length ?? 0) > 0)) {
    const built = ob2FormDefinitionToQuestionnaireStructure(ob2);
    return normalizeQuestionnaireForEditor(code, built);
  }
  if (code === '7') {
    return periodicStructureFromSections('justification-rating-periodic', A7_SECTIONS, {
      subtitle: 'Materiality / risk assessment',
      showRatingSummaryTable: true,
      showSupportingUpload: true,
    });
  }
  if (code === '8') {
    return periodicStructureFromSections('observation-risk-periodic', A8_SECTIONS, {
      subtitle: 'Risk Assessment Form',
      showSupportingUpload: true,
    });
  }
  if (code === '10') {
    return periodicStructureFromSections('comments-periodic', A10_SECTIONS, {
      subtitle: 'Service Provider Periodic Assessment',
      showSupportingUpload: true,
      fieldLabel: 'Details',
    });
  }
  return null;
}

export function usesQuestionnaireStructureEditor(
  apiCode: string,
  def: AnnexureDefinitionPayload
): boolean {
  if (hasQuestionnaireStructure(def)) return true;
  const code = String(apiCode ?? '').trim();
  if (code === '14') return false;
  if (isSystemAnnexureManagementCode(code)) {
    return questionnaireStructureFromPreview(code) != null;
  }
  return annexureCodeUsesDesignerUi(code);
}

function toOb2Question(q: AnnexureQuestionnaireQuestionDef): Ob2AnnexureQuestionnaireQuestion {
  return { ...q };
}

function toOb2FlatQuestion(q: AnnexureFlatQuestionDef): Ob2AnnexureQuestion {
  return {
    id: q.id,
    text: q.text,
    storageRowId: q.storageRowId,
    fullWidth: q.fullWidth,
  };
}

function toOb2Section(s: AnnexureQuestionnaireSectionDef): Ob2AnnexureQuestionnaireSection {
  return {
    id: s.id,
    title: s.title,
    storageRowIds: s.storageRowIds,
    questions: (s.questions ?? []).map(toOb2Question),
    subsections: s.subsections?.map(sub => ({
      id: sub.id,
      title: sub.title,
      questions: sub.questions.map(toOb2Question),
    })),
    completionLabel: s.completionLabel ?? '—',
    weightingScoreDisplay: s.weightingScoreDisplay,
    showSectionalComments: s.showSectionalComments,
    inlineSupportingDocument: s.inlineSupportingDocument,
    inlineSupportingDocumentVariant: s.inlineSupportingDocumentVariant,
    supportingDocumentUploadTone: s.supportingDocumentUploadTone,
    sectionNumberBadge: s.sectionNumberBadge,
    sectionTitleAllCaps: s.sectionTitleAllCaps,
    supportingDocumentTitleMuted: s.supportingDocumentTitleMuted,
    supportingDocumentSizeHint: s.supportingDocumentSizeHint,
    supportingDocumentUploadLinkText: s.supportingDocumentUploadLinkText,
    supportingDocumentStripHeading: s.supportingDocumentStripHeading,
    fullWidthSingleQuestionRow: s.fullWidthSingleQuestionRow,
    sectionFieldLabel: s.sectionFieldLabel,
    sectionUploadSlotId: s.sectionUploadSlotId,
    supportingDocumentBeforeQuestions: s.supportingDocumentBeforeQuestions,
    sectionUploadSlotIdEnd: s.sectionUploadSlotIdEnd,
  };
}

/** Build designer UI definition from saved Structure (Preview / onboarding fill). */
export function questionnaireStructureToOb2FormDefinition(
  apiCode: string,
  structure: AnnexureQuestionnaireStructure | null | undefined
): Ob2AnnexureFormDefinition | undefined {
  if (!structure) return undefined;
  const hasSections = (structure.sections?.length ?? 0) > 0;
  const hasQuestions = (structure.questions?.length ?? 0) > 0;
  if (!hasSections && !hasQuestions) return undefined;

  const ob2Id = ob2AnnexureIdForApiCode(apiCode) ?? getOb2AnnexureIdForApiCode(apiCode);
  if (!ob2Id) return undefined;

  return {
    annexureId: ob2Id,
    variant: (structure.variant ?? 'questionnaire-sections') as Ob2AnnexureFormDefinition['variant'],
    questionnaireUi: structure.questionnaireUi as Ob2AnnexureFormDefinition['questionnaireUi'],
    subtitle: structure.subtitle ?? '',
    fieldLabel: structure.fieldLabel ?? 'Response',
    numberBadge: structure.numberBadge ?? 'square',
    questionnaireLeadHeading: structure.questionnaireLeadHeading,
    questionGridHeading: structure.questionGridHeading,
    showRatingSummaryTable: structure.showRatingSummaryTable,
    showSupportingUpload: structure.showSupportingUpload,
    showApproversStakeholders: structure.showApproversStakeholders,
    sections: hasSections ? structure.sections!.map(toOb2Section) : undefined,
    questions: hasQuestions ? structure.questions!.map(toOb2FlatQuestion) : undefined,
  };
}

/** Resolve Ob2 form for admin preview: saved structure first, then hardcoded template. */
export function ob2FormDefinitionForAdminPreview(
  apiCode: string | undefined,
  payload: AnnexureDefinitionPayload | null | undefined
): Ob2AnnexureFormDefinition | undefined {
  const code = String(apiCode ?? '').trim();
  if (!code) return undefined;
  const fromStructure = questionnaireStructureToOb2FormDefinition(code, payload?.questionnaire);
  if (fromStructure) return fromStructure;
  return ob2AnnexureDefinitionForAdminView(code);
}

/** Onboarding fill: structure from API, with Code 4 vendor-fill section trim. */
export function ob2FormDefinitionForVendorFillFromPayload(
  apiCode: string | undefined,
  payload: AnnexureDefinitionPayload | null | undefined
): Ob2AnnexureFormDefinition | undefined {
  const code = String(apiCode ?? '').trim();
  const fromStructure = questionnaireStructureToOb2FormDefinition(code, payload?.questionnaire);
  if (!fromStructure) return ob2AnnexureDefinitionForVendorFill(code);
  if (fromStructure.questionnaireUi === 'onboarding2Code4' && fromStructure.sections?.length) {
    return {
      ...fromStructure,
      sections: applyCode4VendorFillSections(fromStructure.sections),
    };
  }
  return fromStructure;
}

export function resolvePeriodicSectionsForPreview(
  apiCode: string | undefined,
  payload: AnnexureDefinitionPayload | null | undefined
): OngoingVendorReviewAnnexureSection[] | null {
  const code = String(apiCode ?? '').trim();
  const stored = payload?.questionnaire?.periodicSections;
  if (stored?.length) return periodicDefToOngoing(stored);
  if (code === '6') return A6_SECTIONS;
  if (code === '7') return A7_SECTIONS;
  if (code === '8') return A8_SECTIONS;
  if (code === '10') return A10_SECTIONS;
  if (code === '12') return A12_SECTIONS;
  if (code === '13') return ONGOING_VENDOR_REVIEW_ANNEXURE_FORMS.a13.sections;
  return null;
}

export type ResolvedOngoingPeriodicForm = {
  sections: OngoingVendorReviewAnnexureSection[];
  ratingTableTitle?: string;
  showSupportingDocument?: boolean;
  introText?: string;
};

/** Ongoing / versioned periodic annexure UI: Structure JSON first, then static template. */
export function resolveOngoingPeriodicFormFromDefinition(
  apiCode: string,
  payload: AnnexureDefinitionPayload | null | undefined,
  staticForm: OngoingVendorReviewAnnexureFormDefinition
): ResolvedOngoingPeriodicForm {
  const code = String(apiCode ?? '').trim();
  const sections = resolvePeriodicSectionsForPreview(code, payload) ?? staticForm.sections;
  const q = payload?.questionnaire;
  return {
    sections,
    ratingTableTitle:
      q?.showRatingSummaryTable === false ? undefined : staticForm.ratingTableTitle,
    showSupportingDocument:
      q?.showSupportingUpload === false ? false : staticForm.showSupportingDocument,
  };
}

/** True when saved definition includes periodic sections (not static-template fallback). */
export function annexureFillUsesPeriodicSections(
  apiCode: string | undefined,
  payload: AnnexureDefinitionPayload | null | undefined
): boolean {
  const code = String(apiCode ?? '').trim();
  if (!['6', '8', '10', '12', '13'].includes(code)) return false;
  return (payload?.questionnaire?.periodicSections?.length ?? 0) > 0;
}

/**
 * Whether vendor fill should render {@link PeriodicAnnexureSectionsForm} (aligned with Excel import/export).
 * Uses stored periodic sections when present; otherwise static templates for risk-style annexures.
 */
export function annexureVendorFillUsesPeriodicQuestionnaire(
  apiCode: string | undefined,
  payload: AnnexureDefinitionPayload | null | undefined,
  fillContext: 'onboarding' | 'ongoing' = 'onboarding'
): boolean {
  if (annexureFillUsesPeriodicSections(apiCode, payload)) return true;
  const code = String(apiCode ?? '').trim();
  const sections = resolvePeriodicSectionsForPreview(code, payload);
  if (!sections?.length) return false;
  if (fillContext === 'ongoing') return ['6', '7', '8', '10', '12', '13'].includes(code);
  return ['7', '8', '10'].includes(code);
}

export function resolveIntroTextForAnnexure10(
  payload: AnnexureDefinitionPayload | null | undefined
): string | null {
  const fromQ = payload?.questionnaire?.introText?.trim();
  if (fromQ) return fromQ;
  const fromPayload = payload?.introText?.trim();
  if (fromPayload) return fromPayload;
  return ANNEXURE_10_PREVIEW_INTRO;
}

export function emptyQuestionnaireStructure(apiCode: string): AnnexureQuestionnaireStructure {
  return (
    questionnaireStructureFromPreview(apiCode) ?? {
      variant: 'questionnaire-sections',
      questionnaireUi: 'onboarding2Code4',
      subtitle: '',
      fieldLabel: 'Response',
      numberBadge: 'square',
      sections: [
        {
          id: 'section-1',
          title: 'Section 1',
          completionLabel: '—',
          weightingScoreDisplay: '0.00/100',
          questions: [{ text: 'Question 1', storageRowId: 'q1', required: false }],
        },
      ],
    }
  );
}

/** @deprecated Use questionnaireStructureFromPreview */
export function questionnaireStructureFromOb2AdminView(
  apiCode: string
): AnnexureQuestionnaireStructure | null {
  return questionnaireStructureFromPreview(apiCode);
}

/** @deprecated Prefer normalizeAnnexureDefinitionPayload — kept for imports that only need questionnaire slice. */
export function decodeQuestionnaireStructureStrings(
  q: AnnexureQuestionnaireStructure
): AnnexureQuestionnaireStructure {
  return normalizeAnnexureDefinitionPayload({ questionnaire: q })?.questionnaire ?? q;
}
